/**
 * SAAS-USD-BILLING-1 · Phase 0.5 — Merchant Business-Type selector.
 *
 * Reusable modal that wires the merchant self-service selection UI to the
 * authoritative Phase 0 backend on `PUT /api/merchant/store`.
 *
 * Backend rules (already enforced server-side, we only mirror the UX):
 *   * Only RETAIL and SAAS_DIGITAL_SERVICES are selectable by merchants.
 *   * A locked merchant receives 409 `merchant_type_locked` — we surface a
 *     dedicated read-only state with a Contact Support link instead of a
 *     generic error toast.
 *   * Admin override endpoint is intentionally NOT exposed here.
 *
 * This component does NOT read/write anything billing-adjacent. It is a
 * pure discriminator picker.
 */
import { useState } from "react";
import { X, Store as StoreIcon, Cloud, Lock, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const OPTIONS = [
  {
    value: "RETAIL",
    label: "Retail / Commerce",
    icon: StoreIcon,
    description:
      "Physical products, ecommerce, retail goods, restaurants, travel, local services or similar commerce businesses.",
  },
  {
    value: "SAAS_DIGITAL_SERVICES",
    label: "SaaS / Digital Services",
    icon: Cloud,
    description:
      "Software subscriptions, AI tools, digital services, online platforms, credit/token products or similar digital products.",
  },
];

export const BUSINESS_TYPE_LABEL = {
  UNCLASSIFIED: "Unclassified",
  RETAIL: "Retail / Commerce",
  SAAS_DIGITAL_SERVICES: "SaaS / Digital Services",
};

export default function BusinessTypeSelector({
  open,
  onClose,
  currentValue = "UNCLASSIFIED",
  locked = false,
  onSaved, // (nextValue) => void — receives new merchant_type on success
}) {
  const [choice, setChoice] = useState(null); // 'RETAIL' | 'SAAS_DIGITAL_SERVICES' | null
  const [step, setStep] = useState("pick"); // 'pick' | 'confirm'
  const [busy, setBusy] = useState(false);
  const [runtimeLocked, setRuntimeLocked] = useState(false);

  if (!open) return null;

  const isChanging = currentValue !== "UNCLASSIFIED";
  const showLocked = locked || runtimeLocked;

  async function commit() {
    if (!choice) return;
    setBusy(true);
    try {
      const r = await api.put("/merchant/store", { merchant_type: choice });
      toast.success("Business Type saved");
      onSaved?.(r.data?.merchant_type || choice);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 || detail?.error === "merchant_type_locked") {
        // Server refused — swap to the locked view in-place so the merchant
        // sees the same UX they'd see if we'd known upfront.
        setRuntimeLocked(true);
        onSaved?.(currentValue, { locked: true });
        return;
      }
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.message || "Could not save Business Type. Please try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="business-type-modal"
    >
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden relative">
        <button
          onClick={onClose}
          disabled={busy}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100 text-slate-500 disabled:opacity-40 z-10"
          data-testid="business-type-modal-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {showLocked ? (
          <div className="p-8" data-testid="business-type-locked">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
              <Lock className="w-4 h-4" /> Business Type is locked
            </div>
            <h3 className="font-display text-2xl font-medium tracking-tight text-slate-900 mt-2">
              Business Type is locked
            </h3>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              Your Business Type affects campaign and billing configuration and can no longer be
              changed directly because your account has active or financial activity.
            </p>
            <p className="text-sm text-slate-600 mt-3">
              Current type:{" "}
              <span className="font-semibold text-slate-900">
                {BUSINESS_TYPE_LABEL[currentValue] || "Unclassified"}
              </span>
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <a
                href="mailto:support@cyannect.com?subject=Business%20Type%20change%20request"
                className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5"
                data-testid="business-type-contact-support"
              >
                Contact Support
              </a>
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-full text-sm font-semibold border border-slate-200 hover:bg-slate-50"
                data-testid="business-type-locked-close"
              >
                Close
              </button>
            </div>
          </div>
        ) : step === "pick" ? (
          <div className="p-8" data-testid="business-type-pick">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-cyan-700">
              {isChanging ? "Change Business Type" : "Tell us about your business"}
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900 mt-2">
              What kind of business do you operate?
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              Select your business type so Cyannect can configure the correct campaign and billing
              options for your business.
            </p>

            <div className="grid md:grid-cols-2 gap-3 mt-6">
              {OPTIONS.map((opt) => {
                const active = choice === opt.value;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setChoice(opt.value)}
                    className={`text-left p-4 rounded-xl border transition-all ${
                      active
                        ? "border-cyan-500 ring-2 ring-cyan-200 bg-cyan-50/50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`business-type-option-${opt.value.toLowerCase()}`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-8 h-8 rounded-full inline-flex items-center justify-center ${
                          active ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </span>
                      <div className="font-display text-base font-medium tracking-tight text-slate-900">
                        {opt.label}
                      </div>
                      {active && <Check className="w-4 h-4 text-cyan-600 ml-auto" />}
                    </div>
                    <p className="text-xs text-slate-600 mt-3 leading-relaxed">{opt.description}</p>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-full text-sm font-semibold border border-slate-200 hover:bg-slate-50"
                data-testid="business-type-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep("confirm")}
                disabled={!choice}
                className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="business-type-next"
              >
                Continue
              </button>
            </div>
          </div>
        ) : (
          <div className="p-8" data-testid="business-type-confirm">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-cyan-700">
              Confirm your selection
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900 mt-2">
              You've selected {BUSINESS_TYPE_LABEL[choice]}.
            </h3>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              {choice === "SAAS_DIGITAL_SERVICES"
                ? "We'll configure Cyannect for SaaS / Digital Services — software subscriptions, digital products and platforms."
                : "We'll configure Cyannect for Retail / Commerce — physical products, ecommerce and services."}
            </p>
            {isChanging && (
              <p className="text-xs text-slate-500 mt-3">
                Changing from{" "}
                <span className="font-semibold text-slate-700">
                  {BUSINESS_TYPE_LABEL[currentValue]}
                </span>
                . Once your account starts accruing billing activity, this can only be changed by
                support.
              </p>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setStep("pick")}
                disabled={busy}
                className="px-4 py-2 rounded-full text-sm font-semibold border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
                data-testid="business-type-back"
              >
                Back
              </button>
              <button
                onClick={commit}
                disabled={busy}
                className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-40"
                data-testid="business-type-confirm-btn"
              >
                {busy ? "Saving…" : "Confirm Business Type"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
